// Heads-up on Today: a habit of ours that's working against the goals, said
// plainly with the one change that fixes it. Only shows when there is one.

import { ChevronDown, ChevronUp, Megaphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { GOALS } from '../../content/goals.ts';
import { useEvents, useHousehold, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { validArrival } from '../../domain/firsts.ts';
import { headsUps, type HeadsUp } from '../../domain/headsup.ts';
import { DAY } from '../../domain/time.ts';
import { navigate } from '../../app/router.ts';
import { Card } from '../../ui/primitives.tsx';
import { SourceList } from '../../ui/Sources.tsx';

const SNOOZE_KEY = 'arlo-headsup-snooze';

function snoozed(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(SNOOZE_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

export function useHeadsUps(): HeadsUp[] {
  const events = useEvents();
  const household = useHousehold();
  const profile = useProfile();
  const s = useLiveStatus(5 * 60_000);
  return useMemo(
    () => headsUps({ events, now: s.now, ageWeeks: s.weeks, night: { start: household.nightStart, end: household.nightEnd }, homeAt: validArrival(profile.homeDate, profile.arrivedAt) }),
    [events, s.now, s.weeks, household.nightStart, household.nightEnd, profile.homeDate, profile.arrivedAt],
  );
}

const goalName = (id: string) => GOALS.find((g) => g.id === id)?.title ?? id;

function Item({ h, onSnooze }: { h: HeadsUp; onSnooze: () => void }) {
  return (
    <div className={`headsup-item level-${h.level}`}>
      <strong className="headsup-title">{h.title}</strong>
      <p className="small muted">{h.evidence}</p>
      <p className="small">
        <span className="headsup-label">Why it matters</span> {h.why}
      </p>
      <p className="small headsup-goals">
        {h.goals.map((g) => (
          <span key={g} className="headsup-goal">
            {goalName(g)}
          </span>
        ))}
      </p>
      <p className="small headsup-fix">
        <span className="headsup-label">Try this</span> {h.fix}
      </p>
      <div className="headsup-actions">
        {h.route && (
          <button type="button" className="btn btn-soft btn-sm" onClick={() => navigate(h.route!)}>
            How to
          </button>
        )}
        <button type="button" className="link small" onClick={onSnooze}>
          Got it — remind me in 2 days
        </button>
      </div>
      <SourceList refs={h.sources} />
    </div>
  );
}

export function HeadsUpCard() {
  const all = useHeadsUps();
  const [snooze, setSnooze] = useState(snoozed);
  const [more, setMore] = useState(false);
  const now = Date.now();
  const list = all.filter((h) => !(snooze[h.id] > now));
  if (!list.length) return null;
  const hide = (id: string) => {
    const next = { ...snooze, [id]: now + 2 * DAY };
    setSnooze(next);
    try {
      localStorage.setItem(SNOOZE_KEY, JSON.stringify(next));
    } catch {
      /* private mode: hidden for this visit only */
    }
  };
  const [first, ...rest] = list;
  return (
    <Card className="headsup-card" tone={first.level === 'high' ? 'warn' : 'info'}>
      <header className="headsup-head">
        <span className="eyebrow">
          <Megaphone aria-hidden className="inline-icon" /> Heads-up
        </span>
        {rest.length > 0 && (
          <button type="button" className="link small" onClick={() => setMore(!more)} aria-expanded={more}>
            {more ? 'Fewer' : `${rest.length} more`} {more ? <ChevronUp aria-hidden className="inline-icon" /> : <ChevronDown aria-hidden className="inline-icon" />}
          </button>
        )}
      </header>
      <Item h={first} onSnooze={() => hide(first.id)} />
      {more && rest.map((h) => <Item key={h.id} h={h} onSnooze={() => hide(h.id)} />)}
    </Card>
  );
}
